// Вкладка «Стоп-лист» раздела «Кухня»: какие блюда сейчас в стопе, с каких пор и где (iikoCloud, только чтение).
// Обновление — при открытии вкладки и раз в 2 минуты, пока вкладка видна. Бэкенд кеширует на 90 с и при сбое
// iikoCloud отдаёт последний ответ не старше 15 минут (stale); дольше — ошибка: «стоп-лист недоступен с HH:MM».
//
// Одно блюдо на нескольких кассах — одной строкой («1 и 2 этаж»). Список делится на «Свежие» (в стопе меньше
// FRESH_DAYS дней, новые первыми) и свёрнутый блок «Давно в стопе» — скорее всего, блюдо выведено из меню.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, Skeleton, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import BlockIcon from '@mui/icons-material/Block';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import SegmentedTabs from '../../components/SegmentedTabs';
import StickyFiltersBar from '../../components/StickyFiltersBar';
import FiltersTopRow from '../../components/FiltersTopRow';
import GroupSelector from '../Metrics/components/GroupSelector';
import MetricCard from '../Metrics/components/MetricCard';
import FadeIn from '../../components/motion/FadeIn';
import { useDesktopLayout } from '../../components/layout/AppLayout';
import { fetchKitchenStopList } from '../../services/dataService';
import {
  ACCORDION_BUTTON_SX,
  CARD_LABEL_COLOR,
  CARD_SURFACE_SX,
  DIVIDER_COLOR,
  KPI_COLORS,
  MUTED_TEXT_COLOR,
  MOTION,
  REDUCED_MOTION,
  SECTION_ACCENTS,
} from '../../styles/tokens';
import { selectionTitle } from '../../utils/restaurant';
import { STALE_COLOR, clockTime, formatAge, formatInt as integer, formatSince } from './kitchenFormat';
import { mergeByDish, terminalsLabel } from './stopListModel';
import { useKitchenRestaurants } from './useKitchenRestaurants';
import { useVisiblePolling } from './useVisiblePolling';
import { cacheKey, readCache, writeCache } from './kitchenCache';

const ACCENT = SECTION_ACCENTS['/kitchen'];
const POLL_MS = 2 * 60 * 1000;
const STOP_COLOR = '#ef6b6b';
const LIMITED_COLOR = '#f0a43a';
const DAY_MINUTES = 24 * 60;
// Старше — «Давно в стопе» (возможно, выведено из меню)
const FRESH_DAYS = 7;
const FRESH_MINUTES = FRESH_DAYS * DAY_MINUTES;

const newestFirst = (a, b) => (a.newestMinutes ?? Infinity) - (b.newestMinutes ?? Infinity);

// «Всё» / «Только кухня» — по настройке мест кухни (бар скрыт в «Только кухня»). На десктопе — в строке
// с выбором ресторана и вкладками, той же высоты (36 px); на телефоне — в строке с «Обновлено в …», по содержимому
const SCOPE_OPTIONS = [
  { value: false, label: 'Всё' },
  { value: true, label: 'Только кухня' },
];

function ScopeSwitch({ kitchenOnly, onChange }) {
  return (
    <SegmentedTabs
      variant="filter"
      fullWidth={false}
      tabs={SCOPE_OPTIONS}
      value={kitchenOnly}
      onChange={onChange}
      label="Фильтр"
    />
  );
}

// Плашка только у «Осталось N»: «В стопе» — состояние по умолчанию, его не подписываем
function StatusChip({ item }) {
  if (item.status !== 'limited') return null;
  return (
    <Box
      component="span"
      sx={{ flexShrink: 0, px: 1, py: '1px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', color: LIMITED_COLOR, backgroundColor: 'rgba(240,164,58,0.14)' }}
    >
      Осталось {integer(item.balance)}
    </Box>
  );
}

// Свежие — с полосой слева: на них стоит обратить внимание
const freshStripe = (fresh) => (fresh ? { boxShadow: `inset 3px 0 0 ${STOP_COLOR}` } : {});

// Позиция, которой не было в прошлом опросе, — проявляется на месте; остальные строки не трогаем
const rowIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
`;
const appearSx = (appeared) =>
  appeared ? { animation: `${rowIn} ${MOTION.data}ms ${MOTION.easing}`, [REDUCED_MOTION]: { animation: 'none' } } : {};

// Телефон: две строки — название; «цех · этажи · длительность». По нажатию — детали: с какого времени,
// группа меню, артикул
function StopListRow({ item, localNow, showTerminals, fresh, first, appeared }) {
  const [open, setOpen] = useState(false);
  const summary = [item.place, item.added ? formatAge(item.minutes) : null]
    .filter(Boolean)
    .join(' · ');
  const details = [
    item.added ? `${item.status === 'limited' ? 'Ограничено' : 'В стопе'} ${formatSince(item.added, localNow)}` : null,
    item.menu_group,
    item.article ? `арт. ${item.article}` : null,
    showTerminals ? terminalsLabel(item.terminals) : null,
  ].filter(Boolean).join(' · ');
  return (
    <ButtonBase
      onClick={() => setOpen((v) => !v)}
      aria-expanded={open}
      sx={{
        width: 'calc(100% + 24px)',
        py: 1.25,
        px: 1.5,
        mx: -1.5,
        borderTop: first ? 'none' : `1px solid ${DIVIDER_COLOR}`,
        ...freshStripe(fresh),
        ...appearSx(appeared),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0.5,
        textAlign: 'left',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Typography component="span" sx={{ fontWeight: 600, color: '#fff', minWidth: 0 }}>{item.name}</Typography>
        <StatusChip item={item} />
      </Box>
      <Typography component="span" sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR, fontVariantNumeric: 'tabular-nums' }}>
        {summary}
      </Typography>
      {open && details && (
        <Typography component="span" sx={{ fontSize: '13px', color: CARD_LABEL_COLOR }}>
          {details}
        </Typography>
      )}
    </ButtonBase>
  );
}

// Телефон: сводка — одна строка компактных чипов «число подпись» (прокрутка вбок, если не влезает)
function SummaryChips({ totals, loading }) {
  const chips = [
    { label: 'в стопе', value: totals.stopped, color: STOP_COLOR },
    { label: 'ограничено', value: totals.limited, color: LIMITED_COLOR },
    { label: 'новых за сутки', value: totals.newDay, color: ACCENT.accent },
    { label: 'давно в стопе', value: totals.old, color: MUTED_TEXT_COLOR },
  ];
  return (
    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', mx: -2, px: 2, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
      {chips.map((chip) =>
        loading ? (
          <Skeleton key={chip.label} variant="rounded" width={96} height={34} sx={{ flexShrink: 0, borderRadius: '10px' }} />
        ) : (
          <Box
            key={chip.label}
            sx={{ flexShrink: 0, display: 'flex', alignItems: 'baseline', gap: 0.75, px: 1.25, py: 0.75, borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.06)', whiteSpace: 'nowrap' }}
          >
            <Box component="span" aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: chip.color, alignSelf: 'center' }} />
            <Typography component="span" sx={{ fontSize: '16px', fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
              {integer(chip.value)}
            </Typography>
            <Typography component="span" sx={{ fontSize: '13px', color: CARD_LABEL_COLOR }}>
              {chip.label}
            </Typography>
          </Box>
        )
      )}
    </Box>
  );
}

// Десктоп: компактная таблица, строка ~40 px
// Группы терминалов (кассы) — не колонкой: их названия у каждого клиента свои; они в подсказке у названия
const TABLE_COLUMNS = 'minmax(0, 2.4fr) minmax(0, 1fr) 140px 90px';

function StopListTable({ items, localNow, showTerminals, fresh, appeared }) {
  const cell = { fontSize: '14px', color: CARD_LABEL_COLOR, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  return (
    <Box role="table" aria-label={fresh ? 'Свежие в стопе' : 'Давно в стопе'}>
      <Box role="row" sx={{ display: 'grid', gridTemplateColumns: TABLE_COLUMNS, gap: 2, px: 1.5, py: 0.75, borderBottom: `1px solid ${DIVIDER_COLOR}` }}>
        {['Название', 'Цех', 'С какого времени', 'Сколько'].map((title) => (
          <Typography key={title} role="columnheader" sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR }}>{title}</Typography>
        ))}
      </Box>
      {items.map((item) => (
        <Box
          key={item.product_id}
          role="row"
          sx={{ display: 'grid', gridTemplateColumns: TABLE_COLUMNS, gap: 2, alignItems: 'center', minHeight: 40, px: 1.5, borderBottom: `1px solid ${DIVIDER_COLOR}`, ...freshStripe(fresh), ...appearSx(appeared.has(item.product_id)), '&:last-of-type': { borderBottom: 'none' } }}
        >
          <Box role="cell" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography
              sx={{ ...cell, color: '#fff', fontWeight: 600 }}
              title={[item.menu_group, item.article ? `арт. ${item.article}` : null, showTerminals ? terminalsLabel(item.terminals) : null]
                .filter(Boolean)
                .join(' · ') || undefined}
            >
              {item.name}
            </Typography>
            <StatusChip item={item} />
          </Box>
          <Typography role="cell" sx={cell}>{item.place}</Typography>
          <Typography role="cell" sx={{ ...cell, fontVariantNumeric: 'tabular-nums' }}>{formatSince(item.added, localNow).replace(/^с /, '')}</Typography>
          <Typography role="cell" sx={{ ...cell, fontVariantNumeric: 'tabular-nums' }}>{item.added ? formatAge(item.minutes) : ''}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function StopListBlock({ items, localNow, showTerminals, fresh, desktop, appeared }) {
  return (
    <Box sx={{ ...CARD_SURFACE_SX, px: { xs: 2, md: 1.5 }, py: 0.5, overflow: 'hidden' }}>
      {desktop ? (
        <StopListTable items={items} localNow={localNow} showTerminals={showTerminals} fresh={fresh} appeared={appeared} />
      ) : (
        items.map((item, index) => (
          <StopListRow
            key={item.product_id}
            item={item}
            localNow={localNow}
            showTerminals={showTerminals}
            fresh={fresh}
            first={index === 0}
            appeared={appeared.has(item.product_id)}
          />
        ))
      )}
    </Box>
  );
}

function Notice({ children }) {
  return (
    <Box sx={{ ...CARD_SURFACE_SX, p: 3, textAlign: 'center' }}>
      <Typography sx={{ color: '#e4e4e7' }}>{children}</Typography>
    </Box>
  );
}

// tabs — переключатель «Заказы | Стоп-лист» из KitchenPage: в закреплённой панели под строкой ресторана
export default function StopListTab({ selectedGroup, allGroups, activeServerName, setSelectedGroup, available, tabs = null }) {
  const desktop = useDesktopLayout();
  const { filter, unavailable } = useKitchenRestaurants({ available, selectedGroup, allGroups });
  const [data, setData] = useState(null);
  const [appeared, setAppeared] = useState(() => new Set()); // product_id новых с прошлого опроса
  const [failed, setFailed] = useState(false);
  const [kitchenOnly, setKitchenOnly] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const lastUpdatedRef = useRef(null); // updated_at последнего удачного ответа — для «недоступен с HH:MM»
  const requestRef = useRef(0); // ответ на устаревший запрос (сменился выбор) не применяем
  const knownIdsRef = useRef(null); // product_id прошлого ответа по этому выбору; null — первый ответ
  const token = localStorage.getItem('token');
  const key = useMemo(() => cacheKey('stoplist', token, activeServerName, filter), [token, activeServerName, filter]);

  const load = useCallback(async () => {
    if (!available || !token) return;
    const request = ++requestRef.current;
    try {
      const result = await fetchKitchenStopList(token, filter);
      if (request !== requestRef.current) return;
      // Фоновое обновление — на месте, без затемнения: проявляются только новые позиции
      const ids = new Set((result.items || []).map((i) => i.product_id));
      const known = knownIdsRef.current;
      setAppeared(known ? new Set([...ids].filter((id) => !known.has(id))) : new Set());
      knownIdsRef.current = ids;
      setData(result);
      writeCache(key, result);
      setFailed(false);
      if (result.updated_at) lastUpdatedRef.current = result.updated_at;
    } catch {
      if (request === requestRef.current) setFailed(true);
    }
  }, [available, token, filter, key]);

  // Смена выбора или возврат на вкладку: прежний ответ по этому выбору (если свежий) — сразу, без скелетонов
  useEffect(() => {
    const cached = readCache(key);
    setData(cached);
    knownIdsRef.current = cached ? new Set((cached.items || []).map((i) => i.product_id)) : null;
    setAppeared(new Set());
    setFailed(false);
    load();
  }, [key, load]);
  useVisiblePolling(Boolean(available), POLL_MS, load);

  const dishes = useMemo(
    () => mergeByDish((data?.items || []).filter((item) => !kitchenOnly || item.kitchen)),
    [data, kitchenOnly]
  );
  const fresh = useMemo(() => dishes.filter((d) => (d.newestMinutes ?? 0) < FRESH_MINUTES).sort(newestFirst), [dishes]);
  const old = useMemo(() => dishes.filter((d) => (d.newestMinutes ?? 0) >= FRESH_MINUTES).sort(newestFirst), [dishes]);
  // Итоги — по показанным блюдам (с «Только кухня» — без бара)
  const totals = {
    stopped: dishes.filter((d) => d.status === 'stop').length,
    limited: dishes.filter((d) => d.status === 'limited').length,
    newDay: dishes.filter((d) => d.newestMinutes != null && d.newestMinutes < DAY_MINUTES).length,
    old: old.length,
  };
  const showTerminals = new Set((data?.items || []).map((i) => i.terminal_group)).size > 1;
  const waiting = !data && !failed;
  const localNow = data?.local_now;

  const scopeSwitch = <ScopeSwitch kitchenOnly={kitchenOnly} onChange={setKitchenOnly} />;
  const updatedLabel = data?.updated_at ? (
    <Typography sx={{ fontSize: '13px', color: data.stale ? STALE_COLOR : MUTED_TEXT_COLOR, whiteSpace: 'nowrap' }} role={data.stale ? 'status' : undefined}>
      {data.stale ? `Обновлено в ${clockTime(data.updated_at)}, iikoCloud не отвечает` : `Обновлено в ${clockTime(data.updated_at)}`}
    </Typography>
  ) : null;

  let body;
  if (failed && !data) {
    body = (
      <Notice>
        {lastUpdatedRef.current ? `Стоп-лист недоступен с ${clockTime(lastUpdatedRef.current)}` : 'Стоп-лист сейчас недоступен'} — попробуем
        снова через пару минут.
      </Notice>
    );
  } else if (data?.available === false) {
    body = (
      <Notice>
        {data.reason === 'restaurant_not_connected'
          ? `Ресторан «${selectionTitle(selectedGroup, allGroups || [])}» не подключён к iikoCloud — стоп-листа нет.`
          : 'Для этой группы стоп-лист не настроен — администратор сопоставляет группы в настройках iikoCloud.'}
      </Notice>
    );
  } else {
    body = (
      <>
        {desktop ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 2 }}>
            <MetricCard icon={BlockIcon} title="В стопе" valueNumber={totals.stopped} format={integer} color={STOP_COLOR} loading={waiting} />
            <MetricCard icon={HourglassBottomIcon} title="Ограничено" valueNumber={totals.limited} format={integer} color={KPI_COLORS.discountSum} loading={waiting} />
            <MetricCard icon={NewReleasesOutlinedIcon} title="Новые за сутки" valueNumber={totals.newDay} format={integer} color={ACCENT.accent} loading={waiting} />
            <MetricCard icon={ArchiveOutlinedIcon} title="Давно в стопе" valueNumber={totals.old} format={integer} color={MUTED_TEXT_COLOR} loading={waiting} />
          </Box>
        ) : (
          <SummaryChips totals={totals} loading={waiting} />
        )}

        {!desktop && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            {scopeSwitch}
            {updatedLabel}
          </Box>
        )}

        {data?.offline_terminals?.length > 0 && (
          <Typography role="status" sx={{ fontSize: '13px', color: STALE_COLOR }}>
            {data.offline_terminals.length === 1
              ? `Касса «${data.offline_terminals[0]}» не на связи, данные могут быть устаревшими`
              : `Кассы ${data.offline_terminals.map((n) => `«${n}»`).join(', ')} не на связи, данные могут быть устаревшими`}
          </Typography>
        )}

        {waiting ? (
          <Box sx={{ ...CARD_SURFACE_SX, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }} aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={40} />
            ))}
          </Box>
        ) : dishes.length === 0 ? (
          <Notice>Стоп-лист пуст</Notice>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
            <FadeIn>
              <Box component="section" aria-label={`Свежие — в стопе меньше ${FRESH_DAYS} дней`}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: CARD_LABEL_COLOR, mb: 0.75 }}>
                  Свежие · до {FRESH_DAYS} дней
                </Typography>
                {fresh.length > 0 ? (
                  <StopListBlock items={fresh} localNow={localNow} showTerminals={showTerminals} fresh desktop={desktop} appeared={appeared} />
                ) : (
                  <Notice>Новых позиций в стопе нет</Notice>
                )}
              </Box>
            </FadeIn>

            {old.length > 0 && (
              <Box component="section" aria-label="Давно в стопе">
                <ButtonBase onClick={() => setShowOld((v) => !v)} aria-expanded={showOld} sx={ACCORDION_BUTTON_SX}>
                  <Box component="span" sx={{ minWidth: 0 }}>
                    <Typography component="span" sx={{ display: 'block', fontSize: '15px', fontWeight: 600 }}>
                      Давно в стопе ({old.length})
                    </Typography>
                    <Typography component="span" sx={{ display: 'block', fontSize: '13px', color: MUTED_TEXT_COLOR }}>
                      Больше {FRESH_DAYS} дней — возможно, выведены из меню
                    </Typography>
                  </Box>
                  <KeyboardArrowDownIcon
                    aria-hidden="true"
                    sx={{ flexShrink: 0, transition: 'transform 0.2s ease', transform: showOld ? 'rotate(180deg)' : 'none' }}
                  />
                </ButtonBase>
                {showOld && <StopListBlock items={old} localNow={localNow} showTerminals={showTerminals} fresh={false} desktop={desktop} appeared={appeared} />}
              </Box>
            )}
          </Box>
        )}
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#fff', width: '100%' }}>
      <StickyFiltersBar>
        <FiltersTopRow>
          {/* Десктоп: выбор ресторана, вкладки и «Всё | Только кухня» — одной группой слева, на одной высоте;
              «Обновлено в …» — справа */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, width: { md: '100%' }, maxWidth: '100%' }}>
            {allGroups && (
              <GroupSelector
                groups={allGroups}
                selectedGroup={selectedGroup || 'Все'}
                onSelectGroup={setSelectedGroup}
                activeServerName={activeServerName}
                disabledGroups={unavailable}
                disabledHint="нет в iikoCloud"
                sx={{ mb: 0 }}
              />
            )}
            {desktop && tabs}
            {desktop && data?.available !== false && scopeSwitch}
            {desktop && <Box sx={{ ml: 'auto', pl: 2 }}>{updatedLabel}</Box>}
          </Box>
        </FiltersTopRow>
        {!desktop && tabs}
      </StickyFiltersBar>

      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
        {body}
        <Typography sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR, textAlign: { xs: 'center', md: 'left' } }}>
          Стоп-лист касс iikoCloud, обновляется раз в 2 минуты. Свежие — новые первыми; одно блюдо на нескольких
          кассах — одной строкой.
        </Typography>
      </Box>
    </Box>
  );
}
